import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { User, Lock, Globe, Moon, Sun, Upload, Eye, EyeOff } from 'lucide-react';

const Settings = () => {
  const { t, BACKEND_URL, user, updateProfile, changePassword, language, setLanguage, theme, toggleTheme } = useApp();
  const fileInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    nickname: user?.nickname || '',
    email: user?.email || '',
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [showPasswords, setShowPasswords] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const formData = new FormData();
      if (profileData.nickname !== user.nickname) {
        formData.append('nickname', profileData.nickname);
      }
      if (profileData.email !== user.email) {
        formData.append('email', profileData.email);
      }
      if (avatarFile) {
        formData.append('avatar', avatarFile);
      }

      await updateProfile(formData);
      toast.success(t('settings.profileUpdated'));
      setAvatarFile(null);
      setAvatarPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword) {
      toast.error(t('errors.requiredField'));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('errors.passwordMismatch'));
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(t('errors.minLength').replace('{min}', '6'));
      return;
    }

    setSavingPassword(true);
    try {
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      toast.success(t('settings.passwordChanged'));
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in-up max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
          {t('settings.title')}
        </h1>
        <p className="text-muted-foreground mt-2 font-mono text-xs">
          {t('settings.profile')} • {t('settings.theme')} • {t('settings.language')}
        </p>
      </div>

      {/* Profile Section */}
      <Card className="bezel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            {t('settings.profile')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar */}
          <div className="flex items-center gap-6">
            <Avatar className="w-20 h-20 border-2 border-border">
              <AvatarImage src={avatarPreview || (user?.avatar ? `${user.avatar}` : undefined)} />
              <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                {user?.nickname?.slice(0, 2).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleAvatarChange}
                accept="image/*"
                className="hidden"
                data-testid="avatar-upload-input"
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-avatar-btn">
                <Upload className="w-4 h-4 mr-2" />
                {t('users.avatar')}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">({t('common.optional')})</p>
            </div>
          </div>

          {/* Profile Form */}
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">
                {t('users.login')}
              </Label>
              <Input value={user?.login || ''} disabled className="font-mono bg-muted" />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">
                {t('users.nickname')}
              </Label>
              <Input
                value={profileData.nickname}
                onChange={(e) => setProfileData({ ...profileData, nickname: e.target.value })}
                data-testid="settings-nickname-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">
                {t('users.email')}
              </Label>
              <Input
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                className="font-mono"
                data-testid="settings-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">
                {t('users.role')}
              </Label>
              <Input value={t(`users.${user?.profile_type}`)} disabled className="bg-muted capitalize" />
            </div>
          </div>

          <Button onClick={handleProfileSave} disabled={savingProfile} className="font-display uppercase tracking-[0.14em] glow-amber" data-testid="save-profile-btn">
            {savingProfile ? t('common.loading') : t('settings.saveChanges')}
          </Button>
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card className="bezel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            {t('settings.changePassword')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="eyebrow text-[10px] text-muted-foreground">
              {t('settings.currentPassword')}
            </Label>
            <div className="relative">
              <Input
                type={showPasswords ? 'text' : 'password'}
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                className="font-mono pr-12"
                data-testid="current-password-input"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2"
                onClick={() => setShowPasswords(!showPasswords)}
              >
                {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="eyebrow text-[10px] text-muted-foreground">
              {t('settings.newPassword')}
            </Label>
            <Input
              type={showPasswords ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              className="font-mono"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="eyebrow text-[10px] text-muted-foreground">
              {t('settings.confirmNewPassword')}
            </Label>
            <Input
              type={showPasswords ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              className="font-mono"
              data-testid="confirm-new-password-input"
            />
          </div>

          <Button onClick={handlePasswordSave} disabled={savingPassword} className="font-display uppercase tracking-[0.14em] glow-amber" data-testid="save-password-btn">
            {savingPassword ? t('common.loading') : t('settings.changePassword')}
          </Button>
        </CardContent>
      </Card>

      {/* Appearance Section */}
      <Card className="bezel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            {t('settings.language')} & {t('settings.theme')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Theme Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              <div>
                <p className="font-medium">{t('settings.theme')}</p>
                <p className="text-sm text-muted-foreground">
                  {theme === 'dark' ? t('settings.darkMode') : t('settings.lightMode')}
                </p>
              </div>
            </div>
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              data-testid="theme-toggle-switch"
            />
          </div>

          <Separator />

          {/* Language Select */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5" />
              <div>
                <p className="font-medium">{t('settings.language')}</p>
                <p className="text-sm text-muted-foreground">
                  {language === 'en' ? t('settings.english') : t('settings.french')}
                </p>
              </div>
            </div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-40" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">{t('settings.english')}</SelectItem>
                <SelectItem value="fr">{t('settings.french')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
